import { z } from 'zod';

/**
 * Single timesheet entry awaiting the caller's approval. Hydrated with
 * the names + rate-card-derived amount the approvals page needs to
 * render without a follow-up round-trip.
 */
export const PendingApprovalEntrySchema = z.object({
  approvalInstanceId: z.string().uuid(),
  timesheetId: z.string().uuid(),
  date: z.string(),
  submittedAt: z.string(),
  submittedBy: z.string().uuid(),

  userId: z.string().uuid(),
  userFirstName: z.string(),
  userLastName: z.string(),
  userEmail: z.string(),
  userHoursPerMonth: z.number().nullable(),

  customerId: z.string().uuid(),
  customerName: z.string(),

  productId: z.string().uuid().nullable(),
  productName: z.string().nullable(),

  /** The engagement (SOW) the time is attributed to, and the engagement's
   *  latest-version name. Both null on an unattributed entry, which is a
   *  legitimate submittable state — not missing data. */
  engagementId: z.string().uuid().nullable(),
  engagementName: z.string().nullable(),

  roleId: z.string().uuid(),
  roleName: z.string().nullable(),

  hours: z.number(),
  billable: z.boolean(),
  rate: z.number().nullable(),
  amount: z.number().nullable(),

  description: z.string().nullable(),

  /** True when the entry's timesheet period has locked (and no admin
   *  unlock covers it). A locked entry can't be rejected — the server
   *  vetoes the decision — so the UI disables Reject for it. */
  locked: z.boolean(),
});
export type PendingApprovalEntry = z.infer<typeof PendingApprovalEntrySchema>;

export const PendingApprovalsResultSchema = z.object({
  weekStartDate: z.string().nullable(),
  weekEndDate: z.string().nullable(),
  entries: z.array(PendingApprovalEntrySchema),
  totals: z.object({
    entries: z.number(),
    hours: z.number(),
    amount: z.number(),
  }),
  /** Distinct groups (employees or customers) matching the filter. */
  groupTotal: z.number(),
  /** Groups actually returned; fewer than `groupTotal` ⇒ the cap trimmed the tail. */
  returnedGroups: z.number(),
});
export type PendingApprovalsResult = z.infer<typeof PendingApprovalsResultSchema>;

export interface PendingApprovalsQuery {
  /** Range start (inclusive). Omit both bounds for "all time". */
  weekStartDate?: string;
  /** Range end (inclusive). */
  weekEndDate?: string;
  customerIds?: string[];
  userIds?: string[];
  /** Grouping dimension for the group-atomic cap. */
  groupBy?: 'employee' | 'customer';
  /** Max groups to return; omit to fetch every matching row (week mode). */
  groupLimit?: number;
}

/** Filter for the server-side "approve all" — same shape, no group cap. */
export interface ApproveAllFilter {
  weekStartDate?: string;
  weekEndDate?: string;
  customerIds?: string[];
  userIds?: string[];
}

export interface PendingCount {
  thisWeek: number;
  total: number;
}

/**
 * Query for the "search approved time" dialog. All filters optional; the
 * server scopes results to entries the caller personally approved.
 */
export interface ApprovedSearchQuery {
  fromDate?: string;
  toDate?: string;
  customerIds?: string[];
  userIds?: string[];
  limit?: number;
  offset?: number;
}

/** Approved-search response — same entry shape as pending, paginated. */
export interface ApprovedSearchResult {
  data: PendingApprovalEntry[];
  meta: { total: number; limit: number; offset: number };
}

/** Query for the over-budget signal — same filter shape as the pending list
 *  (minus the group-atomic cap, which doesn't apply here). */
export interface BudgetSignalsQuery {
  weekStartDate?: string;
  weekEndDate?: string;
  customerIds?: string[];
  userIds?: string[];
}

/** How much of ONE entry's hours sit above its pool's allowance, and which
 *  (pool, role, period) — see `BudgetSignalRow` — it counts against. An
 *  entry absent from this list has no signal (within budget, or unbudgeted). */
export interface BudgetSignalEntry {
  timesheetId: string;
  budgetId: string;
  roleId: string;
  periodStart: string;
  overHours: number;
}

/** One (customer, ENGAGEMENT, role, reset-period occurrence) POOL that is over
 *  its allowance.
 *
 *  A budget belongs to a retainer engagement, so each is judged on its own —
 *  `budgetHours` is that agreement's allowance and nothing is summed across
 *  agreements any more. `budgetId` is the pool's correlation key for pairing
 *  entries with rows and for the "N budgets" badge (which counts pools), NOT a
 *  `customer_budgets` row id: two consecutive budgets describing one period
 *  are one pool, and the report has no single row to name.
 *
 *  The numbers come from the same statement the Budget vs Actuals report runs,
 *  so a badge here and that report cannot disagree about one customer and
 *  month. */
export interface BudgetSignalRow {
  budgetId: string;
  engagementId: string | null;
  engagementName: string | null;
  customerId: string;
  customerName: string;
  roleId: string;
  roleName: string | null;
  periodStart: string;
  periodEnd: string;
  periodLabel: string;
  budgetHours: number;
  consumedHours: number;
  overHours: number;
}

export interface BudgetSignalsResult {
  entries: BudgetSignalEntry[];
  budgets: BudgetSignalRow[];
}

export type DecideBulkResultItem =
  | { instanceId: string; ok: true; status: string; alreadyDecided: boolean }
  | { instanceId: string; ok: false; code: string; message: string };

export interface DecideBulkResult {
  results: DecideBulkResultItem[];
}
